import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronDown } from "lucide-react";
import { api, formatApiError, trackEvent } from "@/lib/apiClient";
import Layout, { useSEO } from "@/components/Layout";
import { Section, Overline, BlueRule, Button, Divider } from "@/components/primitives";
import NewsletterSection from "@/components/NewsletterSection";

export default function BookDetail() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [buying, setBuying] = useState(false);
  const [showBuy, setShowBuy] = useState(false);
  const [ci, setCi] = useState({ name: "", email: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/books/${slug}`).then((r) => setBook(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSEO({
    title: book ? (book.seo_title || `${book.title} | Impactful Mindset`) : "Book",
    description: book?.seo_description || book?.short_description,
    image: book?.cover_image,
  });

  const buy = async () => {
    setBuying(true);
    trackEvent("book_cta_click", { book: slug });
    try {
      const { data } = await api.post("/payments/checkout", { book_slug: slug, origin_url: window.location.origin, customer_email: ci.email, customer_name: ci.name });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBuying(false);
    }
  };

  if (notFound) return <Layout><Section className="py-32 text-center"><h1 className="font-serif-display text-3xl">Book not found</h1><Button to="/books" className="mt-6">All Books</Button></Section></Layout>;
  if (!book) return <Layout><Section className="py-32"><div className="h-96 bg-slate-100 animate-pulse" /></Section></Layout>;

  const comingSoon = book.status === "coming_soon";

  return (
    <Layout>
      {/* Hero */}
      <Section className="pt-16 pb-14 border-b border-slate-200">
        <div className="grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-2">
            <div className="aspect-[3/4] bg-slate-100 border border-slate-200 overflow-hidden max-w-sm mx-auto shadow-sm">
              <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-3">
            {comingSoon && <Overline className="!text-slate-400">Coming Soon</Overline>}
            <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 leading-tight text-[#111827]">{book.title}</h1>
            <p className="font-serif-display italic text-xl text-slate-500 mt-4">{book.subtitle}</p>
            <p className="text-slate-600 mt-6 leading-relaxed">{book.short_description}</p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              {comingSoon ? (
                <Button to="/join" data-testid="book-notify">Notify Me</Button>
              ) : book.purchase_url ? (
                <Button as="a" href={book.purchase_url} target="_blank" rel="noopener" data-testid="book-buy-external"
                  onClick={() => trackEvent("book_cta_click", { book: slug })}>{book.primary_cta || "Get the Book"}</Button>
              ) : (
                <Button onClick={() => setShowBuy(true)} disabled={buying} data-testid="book-buy">
                  {`${book.primary_cta || "Get the Book"} · ₱${Number(book.price).toLocaleString()}`}
                </Button>
              )}
              <Button to="/books" variant="secondary">All Books</Button>
            </div>
          </div>
        </div>
      </Section>

      {/* Long description */}
      {book.long_description && (
        <Section className="py-14">
          <div className="im-prose mx-auto" dangerouslySetInnerHTML={{ __html: book.long_description }} />
        </Section>
      )}

      {/* Themes & benefits */}
      <Section className="py-14 bg-[#F8F9FA]">
        <div className="grid md:grid-cols-2 gap-12">
          {book.key_themes?.length > 0 && (
            <div>
              <Overline>Key Themes</Overline>
              <div className="flex flex-wrap gap-2 mt-4">
                {book.key_themes.map((t) => <span key={t} className="border border-slate-300 px-3 py-1.5 text-sm text-slate-700">{t}</span>)}
              </div>
              {book.who_its_for && (
                <div className="mt-8">
                  <Overline>Who It's For</Overline>
                  <p className="text-slate-600 mt-3 leading-relaxed">{book.who_its_for}</p>
                </div>
              )}
            </div>
          )}
          {book.benefits?.length > 0 && (
            <div>
              <Overline>What You'll Take Away</Overline>
              <ul className="mt-4 space-y-3">
                {book.benefits.map((b) => (
                  <li key={b} className="flex gap-3 text-slate-700"><Check className="text-[#0F2C59] shrink-0 mt-0.5" size={18} /> <span>{b}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Section>

      {/* TOC */}
      {book.table_of_contents?.length > 0 && (
        <Section className="py-14">
          <Overline>Inside the Book</Overline>
          <h2 className="font-serif-display text-2xl font-semibold mt-3 text-[#111827]">Chapter overview</h2>
          <div className="mt-6 divide-y divide-slate-200 border-y border-slate-200 max-w-2xl">
            {book.table_of_contents.map((c, i) => (
              <div key={i} className="flex gap-4 py-4">
                <span className="font-serif-display text-[#0F2C59] font-semibold w-8">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-slate-700">{c}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* FAQ */}
      {book.faq?.length > 0 && (
        <Section className="py-14 bg-[#F8F9FA]">
          <Overline>FAQ</Overline>
          <h2 className="font-serif-display text-2xl font-semibold mt-3 mb-6 text-[#111827]">Questions</h2>
          <div className="max-w-2xl divide-y divide-slate-200 border-t border-slate-200">
            {book.faq.map((f, i) => (
              <div key={i} className="py-4">
                <button data-testid={`book-faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between text-left">
                  <span className="font-semibold text-[#111827]">{f.q}</span>
                  <ChevronDown className={`text-[#0F2C59] transition-transform ${openFaq === i ? "rotate-180" : ""}`} size={18} />
                </button>
                {openFaq === i && <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {showBuy && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBuy(false)}>
          <div className="bg-white max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif-display text-xl font-bold text-[#0F2C59]">Complete your purchase</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">We'll send your receipt and ebook access to this email.</p>
            <input data-testid="buy-name" className="w-full border border-slate-300 px-3 py-2 mb-3 text-sm" placeholder="First name" value={ci.name} onChange={(e) => setCi({ ...ci, name: e.target.value })} />
            <input data-testid="buy-email" type="email" required className="w-full border border-slate-300 px-3 py-2 mb-4 text-sm" placeholder="Email address" value={ci.email} onChange={(e) => setCi({ ...ci, email: e.target.value })} />
            <Button onClick={buy} disabled={buying || !ci.email} data-testid="buy-continue" className="w-full">
              {buying ? "Redirecting…" : `Continue to Payment · ₱${Number(book.price).toLocaleString()}`}
            </Button>
          </div>
        </div>
      )}
      <NewsletterSection />
    </Layout>
  );
}
